'use client';

import { useState, useRef, useEffect, useContext } from 'react';
import { ChatContext } from '../contexts/ChatContext';

type Message = { role: 'user' | 'assistant'; content: string; streaming?: boolean };

export default function ChatPanel() {
  const chatContext = useContext(ChatContext);
  const isOpen = chatContext?.isOpen ?? false;
  const closeChat = chatContext?.closeChat ?? (() => {});
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    // Placeholder for assistant message that we'll stream into
    setMessages((prev) => [...prev, { role: 'assistant', content: '', streaming: true }]);

    try {
      const res = await fetch('/internal/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: text }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Feil: ${res.status}`);
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) {
        throw new Error('Kunne ikke lese svar');
      }

      const updateStream = (chunk: string) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'assistant' && last.streaming) {
            next[next.length - 1] = { ...last, content: last.content + chunk };
          }
          return next;
        });
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          try {
            const data = JSON.parse(trimmed) as { response?: string; done?: boolean };
            if (typeof data.response === 'string') {
              updateStream(data.response);
            }
            if (data.done) {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last?.role === 'assistant') {
                  next[next.length - 1] = { ...last, streaming: false };
                }
                return next;
              });
            }
          } catch {
            // skip malformed line
          }
        }
      }

      if (buffer.trim()) {
        try {
          const data = JSON.parse(buffer.trim()) as { response?: string; done?: boolean };
          if (typeof data.response === 'string') updateStream(data.response);
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === 'assistant') next[next.length - 1] = { ...last, streaming: false };
            return next;
          });
        } catch {
          // skip
        }
      }

      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === 'assistant') next[next.length - 1] = { ...last, streaming: false };
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Forespørsel feilet');
      setMessages((prev) => prev.filter((m) => !(m.role === 'assistant' && m.streaming)));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        aria-hidden
        onClick={closeChat}
      />
      <div
        className="fixed inset-0 z-50 flex flex-col w-full h-full bg-[#0d1117] border-0 border-emerald-500/25 shadow-xl overflow-hidden md:inset-auto md:bottom-6 md:right-6 md:left-auto md:top-auto md:w-full md:max-w-md md:h-[32rem] md:rounded-xl md:border"
        role="dialog"
        aria-label="AI Chat"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-emerald-500/25 bg-black/40 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-emerald-400/80"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            AI Chat
          </h2>
          <button
            type="button"
            onClick={closeChat}
            className="p-1.5 text-gray-400 hover:text-gray-200 rounded transition-colors"
            aria-label="Lukk chat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {messages.length === 0 && (
            <p className="text-gray-500 text-sm">
              Skriv et spørsmål og trykk Enter eller Send. Svaret vises fortløpende.
            </p>
          )}
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-500/30'
                    : 'bg-[#0d1117] text-gray-200 border border-emerald-500/20'
                }`}
              >
                <div className="whitespace-pre-wrap break-words">
                  {msg.content}
                  {msg.streaming && (
                    <span className="inline-block w-2 h-4 ml-0.5 bg-emerald-500 animate-pulse align-middle" aria-hidden />
                  )}
                </div>
              </div>
            </div>
          ))}
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-3 border-t border-emerald-500/25 bg-[#0d1117] flex-shrink-0">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Skriv melding..."
              rows={2}
              disabled={loading}
              className="flex-1 px-3 py-2 bg-[#0d1117] border border-emerald-500/20 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/30 resize-none text-sm disabled:opacity-60"
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="px-4 py-2 bg-emerald-500/20 border border-emerald-500/40 hover:bg-emerald-500/30 text-emerald-300 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 self-end"
            >
              {loading ? (
                <>
                  <span className="w-3 h-3 border-2 border-emerald-400/60 border-t-emerald-300 rounded-full animate-spin" />
                  <span>Sender...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
